import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { 
  FaMoneyBillWave, FaFileInvoice, FaReceipt, FaChartLine, 
  FaUsers, FaTruck, FaExchangeAlt, FaWallet, FaCalendarAlt,
  FaArrowUp, FaArrowDown, FaEye, FaDownload, FaChartBar,
  FaChartPie, FaChartArea
} from 'react-icons/fa';
import { 
  BarChart, Bar, PieChart, Pie, Cell, 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer 
} from 'recharts';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./accountDashboard.css";

function AccountDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Summary Cards Data
  const [summaryData, setSummaryData] = useState({
    totalReceivables: 0,
    totalPayables: 0,
    bankBalance: 0,
    cashInHand: 0,
    todayReceipts: 0,
    todayPayments: 0,
    monthlyRevenue: 0,
    monthlyExpenses: 0
  });

  // Recent Transactions
  const [recentTransactions, setRecentTransactions] = useState([]);
  
  // Ledger Summaries
  const [customerLedgerSummary, setCustomerLedgerSummary] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    outstandingAmount: 0
  });

  const [supplierLedgerSummary, setSupplierLedgerSummary] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    outstandingAmount: 0
  });

  // Bank Accounts Summary
  const [bankAccountsSummary, setBankAccountsSummary] = useState([]);

  // Vouchers Summary
  const [vouchersSummary, setVouchersSummary] = useState({
    totalReceiptVouchers: 0,
    totalPaymentVouchers: 0,
    todayReceiptVouchers: 0,
    todayPaymentVouchers: 0
  });

  // Chart Data
  const [monthlyTrendsData, setMonthlyTrendsData] = useState([]);
  const [topCustomersData, setTopCustomersData] = useState([]);
  const [topSuppliersData, setTopSuppliersData] = useState([]);
  const [cashFlowData, setCashFlowData] = useState([]);

  useEffect(() => {
    fetchAllAccountData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllAccountData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSummaryData(),
        fetchBankAccountsSummary()
      ]);
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      const response = await axios.get('/account/dashboard/summary', getHeaders());
      if (response.data.success) {
        const data = response.data.data;
        
        // Map backend data to frontend state
        setSummaryData({
          totalReceivables: data.customer_ledger?.net_receivable || 0,
          totalPayables: data.supplier_ledger?.net_payable || 0,
          bankBalance: data.bank_accounts?.bank_balance || 0,
          cashInHand: data.bank_accounts?.cash_balance || 0,
          todayReceipts: 0, // Will be calculated from recent transactions
          todayPayments: 0, // Will be calculated from recent transactions
          monthlyRevenue: data.sales?.total_sales || 0,
          monthlyExpenses: data.payments?.total_payment_amount || 0
        });
        
        // Set recent transactions
        setRecentTransactions(data.recent_transactions || []);
        
        // Set customer ledger summary
        setCustomerLedgerSummary({
          totalCustomers: data.customer_ledger?.total_customers || 0,
          activeCustomers: data.customer_ledger?.total_customers || 0,
          outstandingAmount: data.customer_ledger?.net_receivable || 0
        });
        
        // Set supplier ledger summary
        setSupplierLedgerSummary({
          totalSuppliers: data.supplier_ledger?.total_suppliers || 0,
          activeSuppliers: data.supplier_ledger?.total_suppliers || 0,
          outstandingAmount: data.supplier_ledger?.net_payable || 0
        });
        
        // Set vouchers summary
        setVouchersSummary({
          totalReceiptVouchers: data.receipts?.total_receipts || 0,
          totalPaymentVouchers: data.payments?.total_payments || 0,
          todayReceiptVouchers: 0,
          todayPaymentVouchers: 0
        });

        // Set chart data from monthly trends
        if (data.monthly_trends) {
          const trendsData = data.monthly_trends.map(item => ({
            month: item.month,
            credits: parseFloat(item.total_credits || 0),
            debits: parseFloat(item.total_debits || 0),
            transactions: parseInt(item.transaction_count || 0)
          }));
          setMonthlyTrendsData(trendsData.reverse());
        }

        // Set top customers data
        if (data.top_customers) {
          const customersData = data.top_customers.map(item => ({
            name: item.customer_name || `Customer ${item.account_id}`,
            outstanding: parseFloat(item.outstanding || 0)
          }));
          setTopCustomersData(customersData);
        }

        // Set top suppliers data
        if (data.top_suppliers) {
          const suppliersData = data.top_suppliers.map(item => ({
            name: item.supplier_name || `Supplier ${item.supplier_id}`,
            outstanding: parseFloat(item.outstanding || 0)
          }));
          setTopSuppliersData(suppliersData);
        }

        // Create cash flow data for pie chart
        const cashFlow = [
          { name: 'Cash Receipts', value: parseFloat(data.receipts?.cash_receipts || 0), color: '#28a745' },
          { name: 'Bank Receipts', value: parseFloat(data.receipts?.bank_receipts || 0), color: '#007bff' },
          { name: 'Cash Payments', value: parseFloat(data.payments?.cash_payments || 0), color: '#dc3545' },
          { name: 'Bank Payments', value: parseFloat(data.payments?.bank_payments || 0), color: '#ffc107' }
        ];
        setCashFlowData(cashFlow);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load dashboard summary');
    }
  };



  const fetchBankAccountsSummary = async () => {
    try {
      const response = await axios.get('/bank-accounts', getHeaders());
      if (response.data.success) {
        setBankAccountsSummary(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };



  const formatCurrency = (amount) => {
    return `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getTransactionTypeBadge = (type) => {
    switch(type) {
      case 'Credit':
        return 'bg-success';
      case 'Debit':
        return 'bg-danger';
      default:
        return 'bg-info';
    }
  };

  const SummaryCard = ({ title, value, icon: Icon, color, trend, onClick }) => (
    <div 
      className={`card border-0 shadow-sm h-100 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      style={{ 
        borderLeft: `4px solid ${color}`,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '12px'
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '';
        }
      }}
    >
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <p className="text-muted mb-2 small text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>{title}</p>
            <h3 className="mb-2 fw-bold" style={{ fontSize: '1.75rem' }}>{formatCurrency(value)}</h3>
            {trend && (
              <small className={`mt-2 d-inline-flex align-items-center ${trend > 0 ? 'text-success' : 'text-danger'}`}>
                {trend > 0 ? <FaArrowUp className="me-1" /> : <FaArrowDown className="me-1" />} 
                <span className="fw-semibold">{Math.abs(trend)}%</span> vs last month
              </small>
            )}
          </div>
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ 
              width: '60px', 
              height: '60px', 
              backgroundColor: `${color}15`,
              color: color,
              flexShrink: 0
            }}
          >
            <Icon size={28} />
          </div>
        </div>
      </div>
    </div>
  );

  const QuickAccessCard = ({ title, icon: Icon, color, path, count }) => (
    <div 
      className="card border-0 shadow-sm h-100 cursor-pointer"
      onClick={() => navigate(path)}
      style={{ 
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <div className="card-body text-center py-4">
        <div 
          className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
          style={{ 
            width: '70px', 
            height: '70px', 
            backgroundColor: `${color}15`,
            color: color,
            border: `3px solid ${color}30`
          }}
        >
          <Icon size={32} />
        </div>
        <h6 className="mb-2 fw-bold">{title}</h6>
        {count !== undefined && (
          <div className="mt-2">
            <span className="badge px-3 py-2" style={{ 
              backgroundColor: `${color}20`, 
              color: color,
              fontSize: '13px',
              fontWeight: '600'
            }}>
              {count} records
            </span>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <Header title="Account Dashboard" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Account Dashboard" />
      <ToastContainer />

      <div className="container-fluid">
        {/* Welcome Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '15px'
            }}>
              <div className="card-body py-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h3 className="mb-2 fw-bold">📊 Account Dashboard</h3>
                    <p className="mb-0 opacity-75">Complete overview of all accounting activities and financial summaries</p>
                  </div>
                  <div>
                    <button 
                      className="btn btn-light btn-sm shadow-sm"
                      onClick={fetchAllAccountData}
                      disabled={loading}
                    >
                      {loading ? (
                        <><span className="spinner-border spinner-border-sm me-2" /> Refreshing...</>
                      ) : (
                        <><FaDownload className="me-2" />Refresh Data</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards Row 1 - with gradient backgrounds */}
        <div className="row mb-4">
          <div className="col-12 mb-3">
            <h5 className="text-muted d-flex align-items-center">
              <FaMoneyBillWave className="me-2" />
              Financial Overview
            </h5>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card summary-card summary-card-purple h-100" onClick={() => navigate('/reports/saleledger')}>
              <div className="card-body summary-card-body">
                <div className="summary-icon-wrapper">
                  <FaMoneyBillWave size={20} className="text-white" />
                </div>
                <p className="summary-label">Total Receivables</p>
                <h2 className="summary-amount">{formatCurrency(summaryData.totalReceivables)}</h2>
                <span className="summary-change-badge badge-success">
                  <FaArrowUp className="me-1" size={8} />
                  View Customer Ledger
                </span>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card summary-card summary-card-pink h-100" onClick={() => navigate('/reports/supplierledger')}>
              <div className="card-body summary-card-body">
                <div className="summary-icon-wrapper">
                  <FaFileInvoice size={20} className="text-white" />
                </div>
                <p className="summary-label">Total Payables</p>
                <h2 className="summary-amount">{formatCurrency(summaryData.totalPayables)}</h2>
                <span className="summary-change-badge badge-danger">
                  <FaEye className="me-1" size={8} />
                  View Supplier Ledger
                </span>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card summary-card summary-card-cyan h-100" onClick={() => navigate('/banking/accounts')}>
              <div className="card-body summary-card-body">
                <div className="summary-icon-wrapper">
                  <FaWallet size={20} className="text-white" />
                </div>
                <p className="summary-label">Bank Balance</p>
                <h2 className="summary-amount">{formatCurrency(summaryData.bankBalance)}</h2>
                <span className="summary-change-badge badge-success">
                  <FaEye className="me-1" size={8} />
                  Manage Bank Accounts
                </span>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card summary-card summary-card-yellow h-100">
              <div className="card-body summary-card-body">
                <div className="summary-icon-wrapper">
                  <FaMoneyBillWave size={20} className="text-white" />
                </div>
                <p className="summary-label">Cash in Hand</p>
                <h2 className="summary-amount">{formatCurrency(summaryData.cashInHand)}</h2>
                <span className="summary-change-badge badge-success">
                  <FaMoneyBillWave className="me-1" size={8} />
                  Available Cash
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards Row 2 */}
        <div className="row mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <SummaryCard 
              title="Today's Receipts"
              value={summaryData.todayReceipts}
              icon={FaReceipt}
              color="#17a2b8"
              onClick={() => navigate('/vouchers/recieptvoucher')}
            />
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <SummaryCard 
              title="Today's Payments"
              value={summaryData.todayPayments}
              icon={FaExchangeAlt}
              color="#6610f2"
              onClick={() => navigate('/vouchers/paymentvoucher')}
            />
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <SummaryCard 
              title="Monthly Revenue"
              value={summaryData.monthlyRevenue}
              icon={FaChartLine}
              color="#20c997"
            />
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <SummaryCard 
              title="Monthly Expenses"
              value={summaryData.monthlyExpenses}
              icon={FaChartBar}
              color="#e83e8c"
            />
          </div>
        </div>

        {/* Quick Access Cards */}
        <div className="row mb-4">
          <div className="col-12 mb-3">
            <h5 className="text-muted">Quick Access</h5>
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="Customer Ledger"
              icon={FaUsers}
              color="#007bff"
              path="/reports/saleledger"
              count={customerLedgerSummary.totalCustomers}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="Supplier Ledger"
              icon={FaTruck}
              color="#28a745"
              path="/reports/supplierledger"
              count={supplierLedgerSummary.totalSuppliers}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="Receipt Vouchers"
              icon={FaReceipt}
              color="#17a2b8"
              path="/vouchers/recieptvoucher"
              count={vouchersSummary.totalReceiptVouchers}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="Payment Vouchers"
              icon={FaFileInvoice}
              color="#6610f2"
              path="/vouchers/paymentvoucher"
              count={vouchersSummary.totalPaymentVouchers}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="Bank Accounts"
              icon={FaWallet}
              color="#ffc107"
              path="/banking/accounts"
              count={bankAccountsSummary.length}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <QuickAccessCard 
              title="General Ledger"
              icon={FaChartLine}
              color="#dc3545"
              path="/expenses/suppliersexpenses"
            />
          </div>
        </div>

        {/* Ledger Summary Section */}
        {false && (
        <div className="row mb-4">
          <div className="col-12 mb-3">
            <h5 className="text-muted d-flex align-items-center">
              <FaUsers className="me-2" />
              Ledger Summaries
            </h5>
          </div>
          <div className="col-lg-6 mb-3">
            <div className="card ledger-card h-100">
              <div className="card-header ledger-card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="ledger-title">
                    <div className="ledger-icon-badge ledger-icon-badge-purple">
                      <FaUsers className="text-white" size={14} />
                    </div>
                    Customer Ledger
                  </h5>
                  <button 
                    className="btn ledger-btn ledger-btn-purple"
                    onClick={() => navigate('/reports/saleledger')}
                  >
                    View Details
                  </button>
                </div>
              </div>
              <div className="card-body ledger-card-body">
                <div className="row g-2">
                  <div className="col-4">
                    <div className="ledger-stat-box">
                      <small className="ledger-stat-label">Total</small>
                      <h3 className="ledger-stat-value ledger-stat-value-purple">{customerLedgerSummary.totalCustomers}</h3>
                      <small className="ledger-stat-subtitle">Customers</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="ledger-stat-box">
                      <small className="ledger-stat-label">Active</small>
                      <h3 className="ledger-stat-value text-success">{customerLedgerSummary.activeCustomers}</h3>
                      <small className="ledger-stat-subtitle">Customers</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="ledger-stat-box-highlight ledger-stat-box-green">
                      <FaMoneyBillWave className="text-success mb-1" size={16} />
                      <h4 className="mb-0 fw-bold text-success" style={{ fontSize: '15px' }}>
                        {formatCurrency(customerLedgerSummary.outstandingAmount)}
                      </h4>
                      <small className="text-success fw-semibold" style={{ fontSize: '9px' }}>Receivables</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-3">
            <div className="card ledger-card h-100">
              <div className="card-header ledger-card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="ledger-title">
                    <div className="ledger-icon-badge ledger-icon-badge-pink">
                      <FaTruck className="text-white" size={14} />
                    </div>
                    Supplier Ledger
                  </h5>
                  <button 
                    className="btn ledger-btn ledger-btn-pink"
                    onClick={() => navigate('/reports/supplierledger')}
                  >
                    View Details
                  </button>
                </div>
              </div>
              <div className="card-body ledger-card-body">
                <div className="row g-2">
                  <div className="col-4">
                    <div className="ledger-stat-box">
                      <small className="ledger-stat-label">Total</small>
                      <h3 className="ledger-stat-value ledger-stat-value-pink">{supplierLedgerSummary.totalSuppliers}</h3>
                      <small className="ledger-stat-subtitle">Suppliers</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="ledger-stat-box">
                      <small className="ledger-stat-label">Active</small>
                      <h3 className="ledger-stat-value text-success">{supplierLedgerSummary.activeSuppliers}</h3>
                      <small className="ledger-stat-subtitle">Suppliers</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="ledger-stat-box-highlight ledger-stat-box-red">
                      <FaMoneyBillWave className="text-danger mb-1" size={16} />
                      <h4 className="mb-0 fw-bold text-danger" style={{ fontSize: '15px' }}>
                        {formatCurrency(supplierLedgerSummary.outstandingAmount)}
                      </h4>
                      <small className="text-danger fw-semibold" style={{ fontSize: '9px' }}>Payables</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Bank Accounts & Recent Transactions */}
        <div className="row mb-4">
          <div className="col-lg-5 mb-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
              <div className="card-header bank-section-header border-0 pt-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="bank-section-title">
                    <FaWallet className="me-2" size={24} />
                    Bank Accounts
                  </h5>
                  <button 
                    className="btn btn-sm btn-light shadow-sm"
                    onClick={() => navigate('/banking/accounts')}
                    style={{ borderRadius: '20px', padding: '6px 16px' }}
                  >
                    <FaEye className="me-1" /> Manage
                  </button>
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {bankAccountsSummary.length === 0 ? (
                  <div className="text-center py-5">
                    <FaWallet size={50} className="text-muted mb-3 opacity-25" />
                    <p className="text-muted">No bank accounts found</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {bankAccountsSummary.slice(0, 5).map((account, index) => (
                      <div key={index} className="bank-account-card">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center flex-fill">
                            <div className="bank-icon-circle">
                              <FaWallet className="text-white" size={20} />
                            </div>
                            <div>
                              <h6 className="bank-account-name">{account.account_name}</h6>
                              <div className="bank-account-number">
                                <strong>{account.bank_name}</strong> - {account.account_number}
                              </div>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="bank-balance-label">Balance</div>
                            <h5 className={`bank-balance-amount ${parseFloat(account.current_balance) >= 0 ? 'bank-balance-positive' : 'bank-balance-negative'}`}>
                              {formatCurrency(account.current_balance)}
                            </h5>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {bankAccountsSummary.length > 5 && (
                  <div className="text-center mt-3">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate('/banking/accounts')}
                      style={{ borderRadius: '20px' }}
                    >
                      View All ({bankAccountsSummary.length}) Accounts
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-7 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-0 pt-3">
                <h6 className="mb-0"><FaCalendarAlt className="me-2" />Recent Transactions</h6>
              </div>
              <div className="card-body">
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {recentTransactions.length === 0 ? (
                    <p className="text-muted text-center">No recent transactions</p>
                  ) : (
                    <table className="table table-hover table-sm">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th style={{ fontSize: '11px' }}>Date</th>
                          <th style={{ fontSize: '11px' }}>Trans ID</th>
                          <th style={{ fontSize: '11px' }}>Type</th>
                          <th style={{ fontSize: '11px' }}>Category</th>
                          <th style={{ fontSize: '11px' }}>Bank</th>
                          <th style={{ fontSize: '11px' }}>Description</th>
                          <th style={{ fontSize: '11px' }} className="text-end">Amount</th>
                          <th style={{ fontSize: '11px' }} className="text-end">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTransactions.map((transaction, index) => (
                          <tr key={transaction.id || index}>
                            <td style={{ fontSize: '10px' }}>{formatDate(transaction.transaction_date)}</td>
                            <td style={{ fontSize: '10px' }}>
                              <span className="badge bg-secondary">{transaction.transaction_id}</span>
                            </td>
                            <td style={{ fontSize: '10px' }}>
                              <span className={`badge ${getTransactionTypeBadge(transaction.transaction_type)}`}>
                                {transaction.transaction_type}
                              </span>
                            </td>
                            <td style={{ fontSize: '10px' }}>
                              <span className="badge bg-info">{transaction.transaction_category}</span>
                            </td>
                            <td style={{ fontSize: '10px' }}>{transaction.bank_name}</td>
                            <td style={{ fontSize: '10px' }}>{transaction.description || '-'}</td>
                            <td style={{ fontSize: '10px' }} className="text-end fw-bold">
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td style={{ fontSize: '10px' }} className="text-end text-muted">
                              {formatCurrency(transaction.balance_after)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Graphs Section */}
        <div className="row mb-4">
          <div className="col-12 mb-3">
            <h5 className="text-muted d-flex align-items-center">
              <FaChartLine className="me-2" />
              Financial Analytics & Trends
            </h5>
          </div>
          
          {/* Monthly Trends Chart */}
          <div className="col-lg-8 mb-3">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white border-0 pt-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <FaChartArea className="me-2 text-primary" />
                  Monthly Transaction Trends
                </h6>
                <small className="text-muted">Last 6 months credit vs debit comparison</small>
              </div>
              <div className="card-body">
                {monthlyTrendsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyTrendsData}>
                      <defs>
                        <linearGradient id="colorCredits" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#28a745" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#28a745" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="colorDebits" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#dc3545" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#dc3545" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#666" style={{ fontSize: '12px' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => `฿${Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="circle"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="credits" 
                        stroke="#28a745" 
                        fillOpacity={1} 
                        fill="url(#colorCredits)" 
                        name="Credits"
                        strokeWidth={2}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="debits" 
                        stroke="#dc3545" 
                        fillOpacity={1} 
                        fill="url(#colorDebits)" 
                        name="Debits"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaChartArea size={40} className="mb-3 opacity-25" />
                    <p>No transaction data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cash Flow Pie Chart */}
          <div className="col-lg-4 mb-3">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white border-0 pt-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <FaChartPie className="me-2 text-success" />
                  Cash Flow Distribution
                </h6>
                <small className="text-muted">Receipts vs Payments breakdown</small>
              </div>
              <div className="card-body">
                {cashFlowData.filter(item => item.value > 0).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={cashFlowData.filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {cashFlowData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => `฿${Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaChartPie size={40} className="mb-3 opacity-25" />
                    <p>No cash flow data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Customers Bar Chart */}
          <div className="col-lg-6 mb-3">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white border-0 pt-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <FaUsers className="me-2 text-info" />
                  Top 5 Customers by Outstanding
                </h6>
                <small className="text-muted">Highest receivables</small>
              </div>
              <div className="card-body">
                {topCustomersData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topCustomersData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" stroke="#666" style={{ fontSize: '12px' }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={120}
                        stroke="#666" 
                        style={{ fontSize: '11px' }} 
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => `฿${Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                      />
                      <Bar dataKey="outstanding" fill="#17a2b8" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaUsers size={40} className="mb-3 opacity-25" />
                    <p>No customer data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Suppliers Bar Chart */}
          <div className="col-lg-6 mb-3">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white border-0 pt-3">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <FaTruck className="me-2 text-warning" />
                  Top 5 Suppliers by Outstanding
                </h6>
                <small className="text-muted">Highest payables</small>
              </div>
              <div className="card-body">
                {topSuppliersData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topSuppliersData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" stroke="#666" style={{ fontSize: '12px' }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={120}
                        stroke="#666" 
                        style={{ fontSize: '11px' }} 
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => `฿${Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                      />
                      <Bar dataKey="outstanding" fill="#ffc107" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaTruck size={40} className="mb-3 opacity-25" />
                    <p>No supplier data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Vouchers Summary */}
        <div className="row mb-4">
          <div className="col-12 mb-3">
            <h5 className="text-muted">Vouchers Summary</h5>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <FaReceipt size={32} className="text-success mb-2" />
                <h3 className="mb-1 fw-bold">{vouchersSummary.totalReceiptVouchers}</h3>
                <p className="text-muted mb-0 small">Total Receipt Vouchers</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <FaFileInvoice size={32} className="text-danger mb-2" />
                <h3 className="mb-1 fw-bold">{vouchersSummary.totalPaymentVouchers}</h3>
                <p className="text-muted mb-0 small">Total Payment Vouchers</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <FaReceipt size={32} className="text-info mb-2" />
                <h3 className="mb-1 fw-bold">{vouchersSummary.todayReceiptVouchers}</h3>
                <p className="text-muted mb-0 small">Today's Receipts</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <FaFileInvoice size={32} className="text-warning mb-2" />
                <h3 className="mb-1 fw-bold">{vouchersSummary.todayPaymentVouchers}</h3>
                <p className="text-muted mb-0 small">Today's Payments</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AccountDashboard;
